import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import { DangerButton } from "components/atoms/DangerButton";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { ErrorWrapper } from "components/organisms/form/BaseField";
import Form from "components/organisms/form/Form";
import FormButton from "components/organisms/form/FormButton";
import TextField from "components/organisms/form/TextField";
import { useDeleteFeatureFlagMutation, useGetFeatureFlagQuery, useUpsertFeatureFlagMutation } from "pages/Features/Feature flags/FeatureFlags.graphql-gen";

import ToggleField from "components/organisms/form/ToggleField";

export const FeatureFlagSchema = z.object({
	displayName: z.string().min(1),
	key: z.string().min(1),
	description: z.string(),
	global: z.boolean(),
	disabledMessage: z.string().optional(),
	enabled: z.boolean(),
	from: z.string().optional(),
	propertiesJson: z.string().optional(),
	to: z.string().optional(),
	version: z.number().optional(),
});

export default function EditFeatureFlagPage() {
	const navigate = useNavigate();
	const { featureId: rawFeatureId } = useParams<{ featureId: string }>();
	const featureId = rawFeatureId === "0" ? undefined : rawFeatureId;
	const [upsertFeatureFlag, { loading: upsertLoading, error }] = useUpsertFeatureFlagMutation();
	const [deleteFeatureFlag, { loading: deleteLoading }] = useDeleteFeatureFlagMutation();
	const { data, loading } = useGetFeatureFlagQuery({
		variables: { featureFlagId: featureId ?? "" },
		skip: !featureId,
	});

	if (featureId && !data?.featureFlag) {
		return <>Laster..</>;
	}

	return (
		<Flex direction={Direction.column} gap="var(--grid-2)">
			<MainLayoutPageHeader
				loading={loading || upsertLoading || deleteLoading}
				title={featureId ? `Rediger ${data?.featureFlag?.displayName ?? "Feature"}` : "Opprett feature"}
				intro={featureId ? `${data?.featureFlag?.usersCount ?? 0} brukere har denne featuren` : ""}
			/>
			<Form
				loading={loading}
				key={featureId}
				value={data?.featureFlag}
				onSubmit={async (featureFlag) => {
					const { error } = await upsertFeatureFlag({
						variables: {
							featureFlag: {
								id: featureId,
								...featureFlag,
							},
						},
					});

					!error && navigate("/features");
				}}
				schema={FeatureFlagSchema}
			>
				<Flex direction={Direction.column} gap="var(--grid-2)">
					<TextField label="Navn" name="displayName" placeholder="Ny feature" />
					<TextField label="Key" name="key" placeholder="nyFeature" />
					<TextField label="Beskrivelse" name="description" placeholder="Hvorfor finnes dette flagget?" />
					<ToggleField label="Global?" name="global" />
					<ToggleField label="Aktivert?" name="enabled" />

					{error && <ErrorWrapper>{error.message}</ErrorWrapper>}
					<FormButton label={featureId ? "Lagre feature" : "Opprett feature"} />
					{featureId && (
						<DangerButton
							label="Slett feature"
							onClick={async () => {
								if (
									!window.confirm(
										`Sikker på at du ønsker å slette featuren?
${data?.featureFlag?.usersCount} brukere har tilgang.`
									)
								) {
									return;
								}

								const { error } = await deleteFeatureFlag({
									variables: { featureFlagId: featureId ?? "" },
								});

								if (error) {
									return console.error(error);
								}

								navigate("/features");
							}}
						/>
					)}
				</Flex>
			</Form>
		</Flex>
	);
}
